import React from "react";
import { LayoutDashboard, Users, FileText, Phone, Network, Monitor, Megaphone, Palette, AlertTriangle, Clock } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

type SidebarProps = {
  collapsed: boolean;
};

const Sidebar: React.FC<SidebarProps> = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const isDashboard = currentPath === "/dashboard";
  const isClients = currentPath.startsWith("/clients");
  const isReports = currentPath.startsWith("/reports");
  const isScreenEditor = currentPath === "/screen-editor"; // ADD THIS
  const isFlightRemovalOffsets = currentPath.startsWith("/flight-removal-offsets");
  const isBillingTransactions = currentPath.startsWith("/billing-transactions");

  const navItems = [
    { path: "/cluster", label: "Cluster", icon: Network },
    { path: "/devices", label: "Device", icon: Monitor },
    { path: "/display-layouts", label: "Display Layouts", icon: Monitor },
    { path: "/ads", label: "Advertisements", icon: Megaphone },
    { path: "/announcements", label: "Announcements", icon: Megaphone },
    // Add Flight Removal Offset item
    { path: "/flight-removal-offsets", label: "Flight Removal Offset", icon: Clock },
    // Add Screen Editor item
    { path: "/screen-editor", label: "Screen Editor", icon: Palette },
  ];

  return (
    <div
      className={`h-full bg-white border-r border-gray-100 shadow-sm flex flex-col transition-all duration-300 ease-in-out ${collapsed ? "-ml-64 w-64" : "ml-0 w-64"
        }`}
      style={{
        transform: collapsed ? "translateX(-100%)" : "translateX(0)",
        opacity: collapsed ? 0 : 1,
      }}
    >
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent p-4">
        {/* Home Section */}
        <div className="border-b border-gray-200 mb-4 pb-3">
          <h3 className="mb-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider px-3">
            Home
          </h3>
          <div className="mb-1">
            <button
              onClick={() => navigate("/dashboard")}
              className={`w-full flex items-center p-3 rounded-lg cursor-pointer ${isDashboard
                ? "bg-blue-50 text-blue-600"
                : "text-gray-700 hover:bg-blue-50/50 hover:text-blue-600"
                }`}
            >
              <LayoutDashboard size={20} className="flex-shrink-0" />
              <span className="block ml-3.5 text-sm font-medium">Dashboard</span>
            </button>
          </div>
        </div>

        {/* Core Modules – single normal tab: Client Management */}
        <div className="border-b border-gray-200 mb-4 pb-3">
          <h3 className="mb-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider px-3">
            Core Modules
          </h3>

          <div className="mb-2.5">
            <button
              onClick={() => navigate("/clients")}
              className={`w-full flex items-center p-3 rounded-lg cursor-pointer ${isClients
                ? "bg-blue-50 text-blue-600"
                : "text-gray-700 hover:bg-blue-50/50 hover:text-blue-600"
                }`}
            >
              <Users size={20} className="flex-shrink-0" />
              <span className="ml-3.5 text-sm font-medium">Client Management</span>
            </button>
          </div>
        </div>

        {/* Device Management */}
        <div className="border-b border-gray-200 mb-4 pb-3">
          <h3 className="mb-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider px-3">
            Device Management
          </h3>

          {navItems.map((route, index) => {
            const isActive = currentPath.startsWith(route.path);

            return (
              <div key={index} className="mb-2.5">
                <button
                  onClick={() => navigate(route.path)}
                  className={`w-full flex items-center p-3 rounded-lg cursor-pointer ${isActive
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-700 hover:bg-blue-50/50 hover:text-blue-600"
                    }`}
                >
                  <route.icon size={20} className="flex-shrink-0" />
                  <span className="ml-3.5 text-sm font-medium">{route.label}</span>
                </button>
              </div>
            );
          })}
        </div>

        {/* Utilities */}
        <div className="mb-4 pb-3">
          <h3 className="mb-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider px-3">
            Utilities
          </h3>

          {/* Billing Transactions */}
          <div className="mb-2.5">
            <button
              onClick={() => navigate("/billing-transactions")}
              className={`w-full flex items-center p-3 rounded-lg cursor-pointer ${isBillingTransactions
                ? "bg-blue-50 text-blue-600"
                : "text-gray-700 hover:bg-blue-50/50 hover:text-blue-600"
                }`}
            >
              <FileText size={16} className="flex-shrink-0" />
              <span className="ml-3.5 text-sm font-medium">
                Billing Transactions
              </span>
            </button>
          </div>

          {/* Reports */}
          <div className="mb-2.5">
            <button
              onClick={() => navigate("/reports")}
              className={`w-full flex items-center p-3 rounded-lg cursor-pointer ${isReports
                ? "bg-blue-50 text-blue-600"
                : "text-gray-700 hover:bg-blue-50/50 hover:text-blue-600"
                }`}
            >
              <FileText size={16} className="flex-shrink-0" />
              <span className="ml-3.5 text-sm font-medium">Reports</span>
            </button>
          </div>

          {/* Help & Support */}
          <div className="mb-2.5">
            <div className="flex items-center p-3 rounded-lg text-gray-500 hover:bg-gray-50">
              <Phone size={20} className="flex-shrink-0" />
              <span className="ml-3.5 text-sm font-medium pointer-events-none">
                Help & Support
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="flex-shrink-0 text-center text-xs text-gray-500 flex items-center justify-center p-3 border-t border-gray-100">
        <img src="/logo192.png" alt="Company Logo" className="w-4 h-4 mr-2" />
        v1.0.0 <span className="text-xs text-gray-400 ml-1.5">by IASB</span>
      </div>
    </div>
  );
};

export default Sidebar;
